import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { GLOBAL_SEARCH, GLOBAL_SEARCH_ASSETS_ENGAGES } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
import { isEntityTypeVisible } from '../utils/entity-types';

// Wire-shape types shared between the top-nav dropdown and the search page.
// See `GLOBAL_SEARCH` + `GLOBAL_SEARCH_ASSETS_ENGAGES` in
// `packages/graphql-client/src/operations/search.ts` — the two operations hit
// the same resolver with different `include` allow-lists, split so the fast
// bucket can paint before the slow assets/engages Atlas call returns.

export interface GlobalSearchResult {
  globalSearch: {
    screens: Array<{
      _id: string;
      UUID?: string | null;
      deviceName?: string;
      name?: string;
      status?: string;
      lastHeartBeat?: string;
      os?: string;
      path?: string | null;
      teamId?: string;
      tags?: string[];
      currentType?: string;
      currentAssetId?: string;
      currentPlaylistId?: string;
      currentScheduleId?: string;
      assignItem?: {
        key?: string;
        value?: string;
        type?: string;
      };
      recentAssignments?: unknown[];
      temporarily?: unknown;
      feature?: unknown;
      scheduleGoLive?: unknown;
      flashMessage?: unknown;
      original?: unknown;
      statusContent?: string;
      isContentExpired?: boolean;
      lastUpdatedDate?: string;
    }>;
    screensTotal: number;
    /** Ids among these rows the caller has favorited — see the backend field. */
    favoriteIds?: string[];
    playlists: Array<{
      _id: string;
      name: string;
      color?: string;
      totalDuration?: number;
      tags?: string[];
      lastUpdatedDate?: string;
      parentGroupInfo?: {
        name?: string;
        path?: string | null;
        teamId?: string;
      };
    }>;
    playlistsTotal: number;
    schedules: Array<{
      _id: string;
      name: string;
      type?: string;
      tags?: string[];
      lastUpdatedDate?: string;
      parentGroupInfo?: {
        name?: string;
        path?: string | null;
        teamId?: string;
      };
    }>;
    schedulesTotal: number;
    operationalSchedules: Array<{
      _id: string;
      name: string;
      type?: string;
      tags?: string[];
      lastUpdatedDate?: string;
      parentGroupInfo?: {
        name?: string;
        path?: string | null;
        teamId?: string;
      };
    }>;
    operationalSchedulesTotal: number;
    folders: Array<{
      _id: string;
      name: string;
      type?: string;
      path?: string;
      /** Screen folder system flag — `true` on the OnHold folder that pauses devices. */
      isHoldDevice?: boolean;
      /** User-level access rules — a non-empty list marks the folder as shared. */
      securities?: Array<{ userId?: string; role?: string }>;
      /** Cross-team sharing — a non-empty list also marks the folder as shared. */
      securityTeams?: string[];
      lastUpdatedDate?: string;
    }>;
    foldersTotal: number;
    feeds: Array<{
      _id: string;
      name: string;
      tags?: string[];
      thumbnail?: string;
      categoryIds?: string[];
      /** FEED_STATUS — 0 = draft/unpublished, 1 = published. */
      status?: number;
      /** Set on curated feeds surfaced in the Featured carousel. */
      featured?: boolean;
    }>;
    feedsTotal: number;
    teams: Array<{
      _id: string;
      name: string;
      /** Optional free-text description — surfaced as an Info-icon tooltip on the row. */
      description?: string;
      /**
       * Team classification chip for the Type column.
       *   'default'   — the account's built-in Default team → renders "Default"
       *   'billing'   — sub-account team (isTeamBilling) → renders "Sub-Account"
       *   'adsPortal' — Ads Portal / Revue → renders "Revue"
       * Undefined = regular team, no chip.
       */
      type?: string;
      memberCount?: number;
      /** Last time the team was modified — feeds the "Updated X ago" recency label. */
      lastUpdatedDate?: string;
    }>;
    teamsTotal: number;
    members: Array<{
      userId?: string;
      name?: string;
      email?: string;
      role?: string;
      teamId: string;
      teamName: string;
      /** Last time this (member, team) pairing was modified — feeds the "Updated X ago" recency label. */
      lastUpdatedDate?: string;
    }>;
    membersTotal: number;
  };
}

export interface GlobalSearchAssetsEngagesResult {
  globalSearch: {
    assets: Array<{
      _id: string;
      name?: string;
      originalFileName?: string;
      type?: string;
      fileType?: string;
      appType?: string;
      webType?: string;
      youtubeType?: string;
      commonType?: string;
      subType?: string;
      serviceType?: string;
      engage?: boolean;
      thumbnail?: string;
      bucket?: string;
      AWSS3ID?: string;
      tags?: string[];
      path?: string | null;
      teamId?: string;
      status?: string;
      contentStatus?: string;
      isHide?: boolean;
      duration?: number;
      emergencyDuration?: number;
      assetExpired?: {
        startDate?: string;
        endDate?: string;
        timezone?: string;
      };
      lastUpdatedDate?: string;
    }>;
    assetsTotal: number;
    /** Ids among these rows the caller has favorited — see the backend field. */
    favoriteIds?: string[];
    engages: Array<{
      _id: string;
      name?: string;
      originalFileName?: string;
      type?: string;
      fileType?: string;
      appType?: string;
      webType?: string;
      youtubeType?: string;
      commonType?: string;
      subType?: string;
      serviceType?: string;
      engage?: boolean;
      thumbnail?: string;
      bucket?: string;
      AWSS3ID?: string;
      tags?: string[];
      path?: string | null;
      teamId?: string;
      status?: string;
      contentStatus?: string;
      isHide?: boolean;
      duration?: number;
      emergencyDuration?: number;
      assetExpired?: {
        startDate?: string;
        endDate?: string;
        timezone?: string;
      };
      lastUpdatedDate?: string;
    }>;
    engagesTotal: number;
  };
}

interface UseGlobalSearchOptions {
  /**
   * Facet filters. The palette only exposes `titleOnly` — `modifiedAfter` and
   * `source` are results-page concerns (you don't reach for a command palette
   * to browse by date), and TYPE is applied by the palette client-side, since
   * both queries hardcode their `include` list in the document.
   */
  filters?: GlobalSearchFilters;
  /** Max rows per section. Dropdown uses 5; page uses 50 for single-type view. */
  limit: number;
}

/**
 * True when any VISIBLE entity bucket has at least one match. Buckets
 * disabled via `HIDDEN_ENTITY_TYPES` (see `entity-types.ts`) do not
 * contribute — so a query that only matches a hidden bucket surfaces the
 * "No results" state instead of an empty container.
 */
function hasVisibleResults(
  results: GlobalSearchResult['globalSearch'] | undefined,
  assetsEngagesResults: GlobalSearchAssetsEngagesResult['globalSearch'] | undefined,
): boolean {
  const only = (type: Parameters<typeof isEntityTypeVisible>[0], n: number | undefined) =>
    isEntityTypeVisible(type) && (n ?? 0) > 0;
  return (
    (!!results &&
      (only('screens', results.screensTotal) ||
        only('playlists', results.playlistsTotal) ||
        only('schedules', results.schedulesTotal) ||
        only('operationalSchedules', results.operationalSchedulesTotal) ||
        only('folders', results.foldersTotal) ||
        only('feeds', results.feedsTotal) ||
        only('teams', results.teamsTotal) ||
        only('members', results.membersTotal))) ||
    (!!assetsEngagesResults &&
      (only('assets', assetsEngagesResults.assetsTotal) ||
        only('engages', assetsEngagesResults.engagesTotal)))
  );
}

/**
 * Two-query global search hook — dropdown-oriented (imperative).
 *
 * Caller drives when to fire via `search(query)`. Used by the top-nav dropdown
 * so it can debounce the input and fire manually — matches the "type-to-search"
 * feel without the hook re-firing on every render.
 *
 * The full search-results page uses `useGlobalSearchAuto` below (variable-
 * driven `useQuery`) because it needs `fetchMore` + stable data across
 * infinite-scroll pages, which `useLazyQuery` doesn't provide cleanly.
 */
export function useGlobalSearch({ limit, filters }: UseGlobalSearchOptions) {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const [executeSearch, { data: liveData, previousData, loading, error, refetch }] =
    useLazyQuery<GlobalSearchResult>(GLOBAL_SEARCH, { fetchPolicy: 'network-only' });

  const [
    executeAssetsEngagesSearch,
    {
      data: liveAssetsEngagesData,
      previousData: previousAssetsEngagesData,
      loading: assetsEngagesLoading,
      error: assetsEngagesError,
      refetch: refetchAssetsEngages,
    },
  ] = useLazyQuery<GlobalSearchAssetsEngagesResult>(GLOBAL_SEARCH_ASSETS_ENGAGES, {
    fetchPolicy: 'network-only',
  });

  // Sticky-fallback pattern — `useLazyQuery` with `fetchPolicy: 'network-only'`
  // briefly emits `data === undefined` while a new query is in flight, which
  // would flash the dropdown empty between keystrokes. `previousData` holds
  // the last successful payload, so serving it as a fallback keeps the
  // dropdown warm during incremental typing (e.g. "benc" → "bench" — same
  // basename, likely very similar results — the user sees an uninterrupted
  // rowset that swaps in place instead of flashing to empty and back).
  const data = liveData ?? previousData;
  const assetsEngagesData = liveAssetsEngagesData ?? previousAssetsEngagesData;

  // Track which query each bucket's `data` corresponds to. Two separate
  // trackers because the fast bucket (`liveData`) and the slow assets/
  // engages bucket (`liveAssetsEngagesData`) race — a new query can land
  // in the fast bucket while the slow bucket still holds the previous
  // query's payload.
  //   - `cachedQuery` (fast tracker) is used at the results-block layer so
  //     incremental typing ("be" → "ben") keeps the cache visible via
  //     prefix match — a stale slow bucket alongside a fresh fast one is
  //     acceptable there (the fresh bucket dominates rendering).
  //   - `slowCachedQuery` is used by stricter consumers (e.g. the footer
  //     "All search results for X" which must not activate while `hasResults`
  //     is being propped up by the slow bucket's still-stale rows).
  //   - `pendingQueryRef` records what `search()` was most recently called
  //     with. Set synchronously inside the callback so the value is always
  //     accurate at the time of the next Apollo response. Apollo v4's
  //     `useLazyQuery` cancels any in-flight prior query on a new
  //     `execute()`, so the response we see always corresponds to the
  //     latest `search()` call.
  const pendingQueryRef = useRef<string>('');
  const [cachedQuery, setCachedQuery] = useState('');
  const [slowCachedQuery, setSlowCachedQuery] = useState('');
  useEffect(() => {
    if (liveData !== undefined) setCachedQuery(pendingQueryRef.current);
  }, [liveData]);
  useEffect(() => {
    if (liveAssetsEngagesData !== undefined) setSlowCachedQuery(pendingQueryRef.current);
  }, [liveAssetsEngagesData]);

  // Destructured so the callback below can depend on the VALUES. Depending on
  // `filters` itself would re-memo on every render (fresh object each time);
  // depending on one field silently pinned the others — which is exactly how
  // the palette's sort menu ended up re-firing the previous ordering.
  const filterTitleOnly = filters?.titleOnly;
  const filterSort = filters?.sort;
  const filterDir = filters?.dir;

  const search = useCallback(
    (query: string) => {
      pendingQueryRef.current = query;
      // Unset facets stay `undefined` so the variable is omitted entirely —
      // callers that never filter keep one cache entry instead of splitting it
      // against an explicit `false`.
      const variables = {
        teamId: activeTeamId,
        query,
        limit,
        titleOnly: filterTitleOnly ? true : undefined,
        sort: filterSort,
        dir: filterDir,
      };
      executeSearch({ variables });
      executeAssetsEngagesSearch({ variables });
    },
    [
      activeTeamId,
      executeSearch,
      executeAssetsEngagesSearch,
      limit,
      filterTitleOnly,
      filterSort,
      filterDir,
    ],
  );

  const retry = useCallback(() => {
    if (refetch) refetch();
    if (refetchAssetsEngages) refetchAssetsEngages();
  }, [refetch, refetchAssetsEngages]);

  // Consumers ultimately read `results` / `assetsEngagesResults`, which unwrap
  // the sticky-effective `data` / `assetsEngagesData` above.
  const results = data?.globalSearch;
  const assetsEngagesResults = assetsEngagesData?.globalSearch;

  // Backed by the backend's *Total fields so pagination past all items in
  // single-type view (items.length === 0 but total > 0) still counts as
  // "results present" — the section renders + "loading more" spinner shows.
  const hasResults = hasVisibleResults(results, assetsEngagesResults);

  return {
    search,
    retry,
    // Fast bucket
    results,
    loading,
    error,
    data,
    // Slow bucket
    assetsEngagesResults,
    assetsEngagesLoading,
    assetsEngagesError,
    assetsEngagesData,
    // Derived
    hasResults,
    /** The query string the current fast-bucket `data` corresponds to —
     *  the last query for which the fast bucket landed a response. Empty
     *  string before any response has landed. Callers gate the results
     *  block on prefix match against this so incremental typing keeps
     *  the cache visible. */
    cachedQuery,
    /** Same as `cachedQuery` but tracks the SLOW (assets/engages) bucket.
     *  Diverges from `cachedQuery` in the fast-lands-first / slow-lands-
     *  later window. Stricter consumers (e.g. the dropdown footer) can
     *  require BOTH trackers to match the current input so `hasResults`
     *  is only trusted when neither bucket is still holding stale rows. */
    slowCachedQuery,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Variable-driven variant for the full search-results page. Uses `useQuery`
// (not `useLazyQuery`) so `fetchMore` behaves canonically — Apollo keeps the
// observable's `data` stable across merges, which is what the infinite-scroll
// UX depends on. The dropdown intentionally stays on the imperative
// `useGlobalSearch` above.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Facet filters, passed straight through to `globalSearch`. Results-page only
 * — the top-nav dropdown deliberately searches unfiltered, so what you type
 * there always shows everything it can find.
 */
export interface GlobalSearchFilters {
  /** ISO-8601. Keeps rows whose `lastUpdatedDate` is at or after this. */
  modifiedAfter?: string;
  /** Keep only rows owned by another team (seen via a shared folder). */
  sharedWithMe?: boolean;
  /** Keep only favorited rows. ANDs with `sharedWithMe`, never ORs. */
  favoritesOnly?: boolean;
  /** Match the entity name only, ignoring tags. */
  titleOnly?: boolean;
  /** `RELEVANCE` | `NAME` | `LAST_MODIFIED` — server-side ordering. */
  sort?: string;
  /** `ASC` | `DESC`. Ignored for RELEVANCE. */
  dir?: string;
}

interface UseGlobalSearchAutoOptions {
  /** Max rows per section. "All" view uses 5; single-type view uses 50. */
  limit: number;
  /** Skip N rows per section — used by single-type view infinite scroll. */
  offset?: number;
  /** The active query string. Empty/short values `skip` the request. */
  query: string;
  /** Minimum query length before firing. Below this, both queries skip. */
  minQueryLength?: number;
  /** Facet filters. Changing one changes the variables, so Apollo refetches. */
  filters?: GlobalSearchFilters;
}

export function useGlobalSearchAuto({
  limit,
  offset = 0,
  query,
  minQueryLength = 2,
  filters,
}: UseGlobalSearchAutoOptions) {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const shouldFire = query.trim().length >= minQueryLength;
  // Undefined (not null/false) for an unset facet so the variable is omitted
  // and the server default applies — sending `titleOnly: false` explicitly
  // would also work, but omitting keeps the query key stable for callers that
  // never filter (the dropdown), so its cache entries aren't split.
  const variables = {
    teamId: activeTeamId,
    query,
    limit,
    offset,
    modifiedAfter: filters?.modifiedAfter,
    sharedWithMe: filters?.sharedWithMe ? true : undefined,
    favoritesOnly: filters?.favoritesOnly ? true : undefined,
    titleOnly: filters?.titleOnly ? true : undefined,
    sort: filters?.sort,
    dir: filters?.dir,
  };

  const {
    data: rawData,
    previousData,
    loading,
    error,
    refetch,
    fetchMore,
  } = useQuery<GlobalSearchResult>(GLOBAL_SEARCH, {
    variables,
    skip: !shouldFire,
    fetchPolicy: 'network-only',
  });

  const {
    data: rawAssetsEngagesData,
    previousData: previousAssetsEngagesData,
    loading: assetsEngagesLoading,
    error: assetsEngagesError,
    refetch: refetchAssetsEngages,
    fetchMore: fetchMoreAssetsEngages,
  } = useQuery<GlobalSearchAssetsEngagesResult>(GLOBAL_SEARCH_ASSETS_ENGAGES, {
    variables,
    skip: !shouldFire,
    fetchPolicy: 'network-only',
  });

  const retry = useCallback(() => {
    if (refetch) refetch();
    if (refetchAssetsEngages) refetchAssetsEngages();
  }, [refetch, refetchAssetsEngages]);

  // Track which query each stream's last successful response was for. Used
  // both for the sticky-data fallback below AND exposed so consumers can
  // gate their own render on a prefix-match against the current input
  // (mirrors the dropdown's `cachedQuery` semantics — see `useGlobalSearch`
  // above). Prefix match keeps rows visible for incremental typing
  // ("be" → "ben") while hiding the cache for unrelated queries
  // ("bench" → "hello") so counts don't flash to stale values.
  const [fastDataQuery, setFastDataQuery] = useState('');
  const [slowDataQuery, setSlowDataQuery] = useState('');
  useEffect(() => {
    if (rawData) setFastDataQuery(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read query only when rawData settles
  }, [rawData]);
  useEffect(() => {
    if (rawAssetsEngagesData) setSlowDataQuery(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read query only when rawAssetsEngagesData settles
  }, [rawAssetsEngagesData]);

  // Sticky data — Apollo v4's `data` can briefly return `undefined` during
  // fetchMore / refetch mid-cycle. `previousData` holds the last successful
  // response; falling back to it keeps the UI populated across the blip so
  // the results section doesn't collapse mid-scroll (which would clamp
  // scrollTop to 0 and produce a "jump to top" visual). Bidirectional
  // prefix match against each bucket's tracker scopes the fallback to
  // same-query pagination + incremental typing ("be" → "ben" keeps rows
  // visible), and hides it for unrelated switches ("bench" → "hello").
  const isFastPrefixMatch =
    fastDataQuery !== '' && (query.startsWith(fastDataQuery) || fastDataQuery.startsWith(query));
  const isSlowPrefixMatch =
    slowDataQuery !== '' && (query.startsWith(slowDataQuery) || slowDataQuery.startsWith(query));
  const data = rawData ?? (isFastPrefixMatch ? previousData : undefined);
  const assetsEngagesData =
    rawAssetsEngagesData ?? (isSlowPrefixMatch ? previousAssetsEngagesData : undefined);
  const results = data?.globalSearch;
  const assetsEngagesResults = assetsEngagesData?.globalSearch;

  const hasResults = hasVisibleResults(results, assetsEngagesResults);

  return {
    retry,
    // Fast bucket
    results,
    loading,
    error,
    data,
    fetchMore,
    // Slow bucket
    assetsEngagesResults,
    assetsEngagesLoading,
    assetsEngagesError,
    assetsEngagesData,
    fetchMoreAssetsEngages,
    // Derived
    hasResults,
    /** The query strings each bucket's current `data` corresponds to. Empty
     *  before that bucket has ever landed. Consumers use these to gate
     *  their own render on strict equality with the current input — e.g.
     *  suppress a "Searching..." spinner when both trackers match the
     *  input (a re-fetch on an already-cached term reveals the same
     *  answer, so a spinner covering it just causes a flicker). */
    fastDataQuery,
    slowDataQuery,
  };
}
